import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Row, Space, Spin, Typography, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import Report from '../components/Report';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useRoleControl } from '../helpers/roleControl';

const { Title, Text } = Typography;

const SavedReportView: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const canEditReports = useRoleControl(['admin', 'user']);
    const [report, setReport] = useState<any | null>(null);
    const [template, setTemplate] = useState<any | null>(null);
    const [areas, setAreas] = useState<any[]>([]);
    const [scopedAreaId, setScopedAreaId] = useState<number | null>(null);
    const [scopedPatternId, setScopedPatternId] = useState<number | null>(null);
    const [scopedPatternPointId, setScopedPatternPointId] = useState<number | null>(null);
    const [scopedMeasurementId, setScopedMeasurementId] = useState<number | null>(null);
    const [loadingReport, setLoadingReport] = useState(true);
    const [loadingTemplate, setLoadingTemplate] = useState(true);
    const [loadingAreas, setLoadingAreas] = useState(true);
    const [reportError, setReportError] = useState<string | null>(null);
    const [templateError, setTemplateError] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        setLoadingAreas(true);
        axios
            .get(ENDPOINTS.AREAS_V2, { withCredentials: true })
            .then((res) => {
                if (!mounted) return;
                setAreas(res.data?.data || res.data || []);
            })
            .catch((err) => {
                console.warn('Failed to load areas for saved report view:', err);
            })
            .finally(() => {
                if (mounted) setLoadingAreas(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!id) {
            setReportError('Missing report id');
            setLoadingReport(false);
            return;
        }
        let mounted = true;
        setLoadingReport(true);
        setReportError(null);
        axios
            .get(ENDPOINTS.REPORT_DETAIL(id), { withCredentials: true })
            .then((res) => {
                if (!mounted) return;
                setReport(res.data?.data || res.data);
            })
            .catch((err) => {
                console.error('Failed to load saved report:', err);
                if (!mounted) return;
                setReportError('Failed to load saved report');
                setReport(null);
            })
            .finally(() => {
                if (mounted) setLoadingReport(false);
            });
        return () => {
            mounted = false;
        };
    }, [id]);

    useEffect(() => {
        if (report?.areaId) {
            setScopedAreaId(report.areaId);
        }
    }, [report]);

    const isReadOnly = !canEditReports;

    useEffect(() => {
        setScopedPatternId(null);
        setScopedPatternPointId(null);
        setScopedMeasurementId(null);
    }, [scopedAreaId]);

    useEffect(() => {
        if (!report) {
            setTemplate(null);
            setLoadingTemplate(false);
            return;
        }
        let mounted = true;
        setLoadingTemplate(true);
        setTemplateError(null);

        const loadTemplate = async () => {
            try {
                const templateId = report.template?.id || report.templateId;
                if (templateId) {
                    const res = await axios.get(ENDPOINTS.REPORT_TEMPLATE_DETAIL(templateId), { withCredentials: true });
                    if (mounted) setTemplate(res.data?.data || res.data);
                    return;
                }

                const lookupKey = report.templateKey || report.reportType;
                if (lookupKey) {
                    const res = await axios.get(ENDPOINTS.REPORT_TEMPLATES, {
                        params: { status: 'active' },
                        withCredentials: true,
                    });
                    const templates = res.data?.data || [];
                    const match = templates.find((t: any) => t.key === lookupKey);
                    if (mounted) setTemplate(match || null);
                    if (mounted && !match) {
                        setTemplateError('Template not found for this saved report');
                    }
                    return;
                }

                if (mounted) {
                    setTemplate(null);
                    setTemplateError('Template reference missing on saved report');
                }
            } catch (err) {
                console.error('Failed to load report template:', err);
                if (mounted) {
                    setTemplate(null);
                    setTemplateError('Failed to load report template');
                }
            } finally {
                if (mounted) setLoadingTemplate(false);
            }
        };

        loadTemplate();
        return () => {
            mounted = false;
        };
    }, [report]);

    const parentById = useMemo(() => {
        const map = new Map<number, number | null>();
        areas.forEach((area: any) => {
            if (area?.id) {
                map.set(area.id, area.parentAreaId ?? null);
            }
        });
        return map;
    }, [areas]);

    const scopedAreaName = useMemo(() => {
        if (!scopedAreaId) return report?.area?.name ?? null;
        const match = areas.find((area) => area?.id === scopedAreaId);
        return match?.name ?? report?.area?.name ?? null;
    }, [areas, report, scopedAreaId]);

    const resolveTemplateAreaTypeIds = useCallback((candidate: any) => {
        if (Array.isArray(candidate?.areaTypeIds)) {
            return candidate.areaTypeIds;
        }
        if (Array.isArray(candidate?.metadata?.appliesToAreaTypes)) {
            return candidate.metadata.appliesToAreaTypes;
        }
        if (Array.isArray(candidate?.areaTypes)) {
            return candidate.areaTypes
                .map((entry: any) => entry?.areaType?.id ?? entry?.areaTypeId)
                .filter((id: number | undefined) => typeof id === 'number');
        }
        return [];
    }, []);

    const templateSupportsArea = useCallback((candidate: any, area: any) => {
        if (!candidate || !area?.areaType?.id) return false;
        const templateAreaTypeIds = resolveTemplateAreaTypeIds(candidate);
        if (!templateAreaTypeIds || templateAreaTypeIds.length === 0) {
            return true;
        }
        return templateAreaTypeIds.includes(area.areaType.id);
    }, [resolveTemplateAreaTypeIds]);

    const selectTemplateForNavigation = useCallback(async (params: {
        areaId: number;
        navigationContext: 'area' | 'pattern' | 'point' | 'measurement';
        patternId?: number;
        pointId?: number;
        measurementId?: number;
        force?: boolean;
    }) => {
        const area = areas.find((item) => item?.id === params.areaId);
        if (!area?.areaType?.id) return;
        if (!params.force && params.navigationContext === 'area' && templateSupportsArea(template, area)) {
            return;
        }

        setLoadingTemplate(true);
        setTemplateError(null);
        try {
            const res = await axios.get(ENDPOINTS.REPORT_TEMPLATES, {
                params: {
                    areaTypeId: area.areaType.id,
                    status: 'active',
                    navigationContext: params.navigationContext,
                    patternId: params.patternId,
                    pointId: params.pointId,
                    measurementId: params.measurementId,
                },
                withCredentials: true,
            });
            const templates = res.data?.data || res.data || [];
            if (!Array.isArray(templates) || templates.length === 0) {
                setTemplateError('No templates available for this navigation');
                message.warning('No templates available for this navigation');
                return;
            }

            const findSpecificTemplate = () => {
                if (params.navigationContext === 'measurement' && params.measurementId) {
                    const exact = templates.find((item: any) =>
                        Array.isArray(item?.measurements) &&
                        item.measurements.some((m: any) => m?.measurement?.id === params.measurementId)
                    );
                    if (exact) return exact;
                    return templates.find((item: any) => Array.isArray(item?.measurements) && item.measurements.length > 0) || null;
                }
                if (params.navigationContext === 'pattern' && params.patternId) {
                    const exact = templates.find((item: any) =>
                        Array.isArray(item?.patterns) &&
                        item.patterns.some((p: any) => p?.pattern?.id === params.patternId)
                    );
                    if (exact) return exact;
                    return templates.find((item: any) => Array.isArray(item?.patterns) && item.patterns.length > 0) || null;
                }
                if (params.navigationContext === 'point' && params.pointId) {
                    const exact = templates.find((item: any) =>
                        Array.isArray(item?.patternPoints) &&
                        item.patternPoints.some((p: any) => p?.patternPoint?.id === params.pointId)
                    );
                    if (exact) return exact;
                    return templates.find((item: any) => Array.isArray(item?.patternPoints) && item.patternPoints.length > 0) || null;
                }
                return null;
            };

            let nextTemplate: any = null;
            if (params.navigationContext === 'area') {
                const lookupKey = report?.templateKey || report?.reportType;
                const matchedTemplate = lookupKey
                    ? templates.find((item: any) => item?.key === lookupKey)
                    : null;
                const currentTemplate = template
                    ? templates.find((item: any) => item?.id === template?.id)
                    : null;
                nextTemplate = matchedTemplate || currentTemplate || templates[0];
                if (!matchedTemplate && !currentTemplate && templates.length > 1) {
                    message.info('Using default template for this area');
                }
            } else {
                nextTemplate = findSpecificTemplate() || templates[0];
                if (!nextTemplate && templates.length > 1) {
                    message.info('Using default template for this navigation');
                }
            }

            if (nextTemplate) {
                setTemplate(nextTemplate);
            }
        } catch (err) {
            console.error('Failed to load templates for navigation:', err);
            message.error('Failed to load templates for this navigation');
        } finally {
            setLoadingTemplate(false);
        }
    }, [areas, report, template, templateSupportsArea]);

    const isDescendant = useCallback((candidateId: number) => {
        if (!report?.areaId) return false;
        let current: number | null = candidateId;
        while (current) {
            if (current === report.areaId) return true;
            current = parentById.get(current) ?? null;
        }
        return false;
    }, [parentById, report]);

    const handleAreaClick = useCallback((areaId: number) => {
        if (!report?.areaId) return;
        if (!isDescendant(areaId)) {
            message.info('Area navigation is limited to child areas for saved reports.');
            return;
        }
        if (isReadOnly) {
            setScopedAreaId(areaId);
            void selectTemplateForNavigation({
                areaId,
                navigationContext: 'area',
            });
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        navigate('/reports', {
            state: {
                dashboardNav: {
                    areaId,
                    dateFrom: report.dateFrom,
                    dateTo: report.dateTo,
                },
            },
        });
    }, [isDescendant, isReadOnly, navigate, report, selectTemplateForNavigation]);

    const handleMetricClick = useCallback((metricKey: string, metricId: number | undefined) => {
        if (!report?.areaId) return;
        if (isReadOnly) {
            message.info('Metric navigation is not available in read-only view.');
            return;
        }
        navigate('/reports', {
            state: {
                dashboardNav: {
                    areaId: report.areaId,
                    metricKey,
                    metricId,
                    dateFrom: report.dateFrom,
                    dateTo: report.dateTo,
                },
            },
        });
    }, [isReadOnly, navigate, report]);

    const handlePatternClick = useCallback((patternId: number, areaId: number) => {
        if (!report?.areaId) return;
        if (!isDescendant(areaId)) {
            message.info('Pattern navigation is limited to child areas for saved reports.');
            return;
        }
        setScopedAreaId(areaId);
        void selectTemplateForNavigation({
            areaId,
            navigationContext: 'pattern',
            patternId,
            force: true,
        });
        setScopedPatternId(patternId);
        setScopedPatternPointId(null);
        setScopedMeasurementId(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [isDescendant, report, selectTemplateForNavigation]);

    const handlePatternPointClick = useCallback((
        patternPointId: number,
        patternId: number,
        areaId: number
    ) => {
        if (!report?.areaId) return;
        if (!isDescendant(areaId)) {
            message.info('Pattern point navigation is limited to child areas for saved reports.');
            return;
        }
        setScopedAreaId(areaId);
        void selectTemplateForNavigation({
            areaId,
            navigationContext: 'point',
            pointId: patternPointId,
            force: true,
        });
        setScopedPatternId(patternId);
        setScopedPatternPointId(patternPointId);
        setScopedMeasurementId(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [isDescendant, report, selectTemplateForNavigation]);

    const handleMeasurementClick = useCallback((measurementId: number, areaId?: number | null) => {
        if (!report?.areaId) return;
        const targetAreaId = areaId ?? scopedAreaId ?? report.areaId;
        if (!isDescendant(targetAreaId)) {
            message.info('Measurement navigation is limited to child areas for saved reports.');
            return;
        }
        setScopedAreaId(targetAreaId);
        void selectTemplateForNavigation({
            areaId: targetAreaId,
            navigationContext: 'measurement',
            measurementId,
            force: true,
        });
        setScopedMeasurementId(measurementId);
        setScopedPatternId(null);
        setScopedPatternPointId(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [isDescendant, report, scopedAreaId, selectTemplateForNavigation]);

    const isLoading = loadingReport || loadingTemplate || loadingAreas;

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin tip="Loading saved report..." />
            </div>
        );
    }

    if (reportError) {
        return (
            <div style={{ padding: 24 }}>
                <Alert type="error" message={reportError} />
            </div>
        );
    }

    if (!report || !template) {
        return (
            <div style={{ padding: 24 }}>
                <Alert type="warning" message={templateError || 'Saved report is missing a template.'} />
            </div>
        );
    }

    return (
        <div className="saved-report-view-page" style={{ padding: 24 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} className="saved-report-view-header">
                <Space direction="vertical" size={0}>
                    <Title level={3} style={{ margin: 0 }}>
                        {report.title || 'Saved Report'}
                    </Title>
                    <Text type="secondary">
                        {(scopedAreaName || `Area ${scopedAreaId || report.areaId || '—'}`)} · {dayjs(report.dateFrom).format('YYYY-MM-DD')} → {dayjs(report.dateTo).format('YYYY-MM-DD')}
                    </Text>
                </Space>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/saved-reports')}>
                    Back to Saved Reports
                </Button>
            </Row>
            <Card className="saved-report-view-report-card">
                <Report
                    template={template}
                    areaId={scopedAreaId ?? report.areaId}
                    dateFrom={report.dateFrom}
                    dateTo={report.dateTo}
                    patternId={scopedPatternId ?? undefined}
                    patternPointId={scopedPatternPointId ?? undefined}
                    measurementId={scopedMeasurementId ?? undefined}
                    showControls={false}
                    lockScope={isReadOnly}
                    onAreaClick={handleAreaClick}
                    onMetricClick={handleMetricClick}
                    onPatternClick={handlePatternClick}
                    onPatternPointClick={handlePatternPointClick}
                    onMeasurementClick={handleMeasurementClick}
                />
            </Card>
        </div>
    );
};

export default SavedReportView;
